import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Plus, Edit2, X, Upload, Image as ImageIcon, Eye, EyeOff, Ticket, Trash2, Gift, Percent, Tag, Target, Calendar, Store } from 'lucide-react';
import api from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { formatAmount } from '../../lib/currencyUtils';

const VOUCHER_CATEGORIES = [
  { value: 'gift', label: 'Gift Voucher', icon: Gift, color: '#D4AF37', desc: 'Fixed monetary value, purchasable' },
  { value: 'cashback', label: 'Cashback', icon: Percent, color: '#16a34a', desc: 'Auto-issued as % of purchase' },
  { value: 'category', label: 'Category', icon: Tag, color: '#7c3aed', desc: 'Restricted to store categories' },
  { value: 'milestone', label: 'Milestone', icon: Target, color: '#ea580c', desc: 'Issued at spend threshold' },
  { value: 'event', label: 'Event', icon: Calendar, color: '#0284c7', desc: 'Time-bound campaign' },
  { value: 'tenant_sponsored', label: 'Tenant-Sponsored', icon: Store, color: '#be185d', desc: 'Funded by a specific tenant' },
];

const STORE_CATEGORIES = ['F&B', 'Fashion', 'Electronics', 'Health & Beauty', 'Entertainment', 'Home & Living', 'Sports', 'Services', 'Grocery', 'Other'];

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl my-4">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="font-heading text-lg font-bold text-[#561D1A]">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5 max-h-[75vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

export default function VoucherTypesTab() {
  const { currency: activeCurrency, symbol, formatShortDate } = useCurrency();
  const [types, setTypes] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editType, setEditType] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', store_ids: [], allowed_amounts: '500,1000,2000,5000',
    image_url: '', artwork_url: '', voucher_category: 'gift', is_purchasable: true,
    cashback_percent: '', allowed_store_categories: [], milestone_threshold: '', milestone_reward_amount: '',
    campaign_start: '', campaign_end: '', sponsor_store_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingArtwork, setUploadingArtwork] = useState(false);
  const imageInputRef = useRef(null);
  const artworkInputRef = useRef(null);
  // Create Vouchers state
  const [createVoucherModal, setCreateVoucherModal] = useState(null); // type object or null
  const [voucherForm, setVoucherForm] = useState({ amount: '', quantity: 1, expiry_days: 365 });
  const [creatingVouchers, setCreatingVouchers] = useState(false);

  const load = async () => {
    const [typesRes, storesRes] = await Promise.all([
      api.get('/api/voucher/admin/voucher-types').catch(() => ({ data: [] })),
      api.get('/api/store/admin/stores').catch(() => ({ data: [] })),
    ]);
    setTypes(typesRes.data);
    setStores(storesRes.data.filter(s => s.is_active));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditType(null);
    setForm({
      name: '', description: '', store_ids: [], allowed_amounts: '500,1000,2000,5000',
      image_url: '', artwork_url: '', voucher_category: 'gift', is_purchasable: true,
      cashback_percent: '', allowed_store_categories: [], milestone_threshold: '', milestone_reward_amount: '',
      campaign_start: '', campaign_end: '', sponsor_store_id: '',
    });
    setModalOpen(true);
  };

  const openEdit = (t) => {
    setEditType(t);
    setForm({
      name: t.name, description: t.description || '',
      store_ids: t.store_ids || [],
      allowed_amounts: t.allowed_amounts?.join(',') || '',
      image_url: t.image_url || '', artwork_url: t.artwork_url || '',
      voucher_category: t.voucher_category || 'gift',
      is_purchasable: t.is_purchasable !== false,
      cashback_percent: t.cashback_percent || '',
      allowed_store_categories: t.allowed_store_categories || [],
      milestone_threshold: t.milestone_threshold || '',
      milestone_reward_amount: t.milestone_reward_amount || '',
      campaign_start: t.campaign_start ? t.campaign_start.slice(0, 16) : '',
      campaign_end: t.campaign_end ? t.campaign_end.slice(0, 16) : '',
      sponsor_store_id: t.sponsor_store_id || '',
    });
    setModalOpen(true);
  };

  const handleUpload = async (file, type) => {
    if (!file) return;
    const setter = type === 'image' ? setUploadingImage : setUploadingArtwork;
    setter(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/api/config/storage/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const field = type === 'image' ? 'image_url' : 'artwork_url';
      setForm(f => ({ ...f, [field]: res.data.url }));
      toast.success(`${type === 'image' ? 'Image' : 'Artwork'} uploaded`);
    } catch (err) {
      toast.error(err.displayMessage || 'Upload failed');
    } finally { setter(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const amounts = form.allowed_amounts.split(',').map(n => parseFloat(n.trim())).filter(n => !isNaN(n) && n > 0);
    const payload = {
      name: form.name, description: form.description,
      store_ids: form.store_ids, allowed_amounts: amounts,
      image_url: form.image_url || null, artwork_url: form.artwork_url || null,
      voucher_category: form.voucher_category,
      is_purchasable: form.voucher_category === 'cashback' || form.voucher_category === 'milestone' ? false : form.is_purchasable,
      cashback_percent: form.cashback_percent ? parseFloat(form.cashback_percent) : null,
      allowed_store_categories: form.allowed_store_categories,
      milestone_threshold: form.milestone_threshold ? parseFloat(form.milestone_threshold) : null,
      milestone_reward_amount: form.milestone_reward_amount ? parseFloat(form.milestone_reward_amount) : null,
      campaign_start: form.campaign_start ? new Date(form.campaign_start).toISOString() : null,
      campaign_end: form.campaign_end ? new Date(form.campaign_end).toISOString() : null,
      sponsor_store_id: form.sponsor_store_id || null,
    };
    try {
      if (editType) {
        await api.put(`/api/voucher/admin/voucher-types/${editType.id}`, payload);
        toast.success('Voucher type updated');
      } else {
        await api.post('/api/voucher/admin/voucher-types', payload);
        toast.success('Voucher type created');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.displayMessage || 'Failed to save');
    } finally { setSaving(false); }
  };

  const toggleStore = (id) => {
    setForm(f => ({
      ...f,
      store_ids: f.store_ids.includes(id) ? f.store_ids.filter(s => s !== id) : [...f.store_ids, id]
    }));
  };

  const toggleStoreCategory = (cat) => {
    setForm(f => ({
      ...f,
      allowed_store_categories: f.allowed_store_categories.includes(cat)
        ? f.allowed_store_categories.filter(c => c !== cat)
        : [...f.allowed_store_categories, cat]
    }));
  };

  const openCreateVouchers = (type) => {
    setCreateVoucherModal(type);
    setVoucherForm({ amount: '', quantity: 1, expiry_days: 365 });
  };

  const handleCreateVouchers = async (e) => {
    e.preventDefault();
    setCreatingVouchers(true);
    try {
      const res = await api.post('/api/voucher/admin/vouchers/generate', {
        type_id: createVoucherModal.id,
        amount: parseFloat(voucherForm.amount),
        quantity: parseInt(voucherForm.quantity),
        expiry_days: parseInt(voucherForm.expiry_days),
      });
      toast.success(`${res.data.created} voucher(s) created for ${createVoucherModal.name}`);
      setCreateVoucherModal(null);
    } catch (err) {
      toast.error(err.displayMessage || 'Failed to create vouchers');
    } finally { setCreatingVouchers(false); }
  };

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const bulkImportRef = useRef(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(prev => prev.size === types.length ? new Set() : new Set(types.map(t => t.id)));
  const handleBulkDelete = async () => {
    if (!selected.size || !window.confirm(`Delete ${selected.size} voucher type(s) permanently?`)) return;
    setBulkDeleting(true);
    try {
      const res = await api.post('/api/voucher/admin/voucher-types/bulk-delete', { ids: [...selected] });
      if (res.data.deleted > 0) toast.success(`${res.data.deleted} type(s) deleted`);
      if (res.data.skipped?.length) {
        res.data.skipped.forEach(msg => toast.error(`Cannot delete: ${msg}`));
      }
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(err.displayMessage || 'Bulk delete failed');
    } finally { setBulkDeleting(false); }
  };

  const handleDelete = async (type) => {
    if (!window.confirm(`Delete "${type.name}" permanently?`)) return;
    setDeleting(type.id);
    try {
      const res = await api.delete(`/api/voucher/admin/voucher-types/${type.id}`);
      toast.success(res.data.message || 'Voucher type deleted');
      setSelected(prev => { const n = new Set(prev); n.delete(type.id); return n; });
      load();
    } catch (err) {
      toast.error(err.displayMessage || 'Failed to delete');
    } finally { setDeleting(null); }
  };

  const handleBulkVoucherImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/api/voucher/admin/vouchers/bulk-import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${res.data.created} voucher(s)${res.data.errors?.length ? `, ${res.data.errors.length} error(s)` : ''}`);
      if (res.data.errors?.length) {
        res.data.errors.forEach(err => toast.error(`Row ${err.row}: ${err.error}`));
      }
    } catch (err) { toast.error(err.displayMessage || 'Import failed'); }
    finally { setBulkImporting(false); e.target.value = ''; }
  };

  const handleDownloadVoucherTemplate = async () => {
    try {
      const res = await api.get('/api/voucher/admin/vouchers/csv-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = 'vouchers_import_template.csv';
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  return (
    <div data-testid="voucher-types-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-sm text-gray-500">{types.length} type(s)</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={handleDownloadVoucherTemplate}
            className="flex items-center gap-1 text-xs text-gray-600 hover:text-[#561D1A] px-3 py-2 rounded-xl border border-gray-200 hover:border-[#561D1A]/30" data-testid="download-voucher-template-btn">
            <Upload size={13} /> CSV Template
          </button>
          <input ref={bulkImportRef} type="file" accept=".csv" className="hidden" onChange={handleBulkVoucherImport} />
          <button onClick={() => bulkImportRef.current?.click()} disabled={bulkImporting}
            className="flex items-center gap-1 text-xs text-[#561D1A] hover:text-white hover:bg-[#561D1A] px-3 py-2 rounded-xl border border-[#561D1A]/30 transition-colors disabled:opacity-50" data-testid="bulk-import-vouchers-btn">
            <Upload size={13} /> {bulkImporting ? 'Importing...' : 'Bulk Import'}
          </button>
          <button onClick={openCreate} className="flex items-center gap-2 bg-[#EC1A23] text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-[#C4121A]" data-testid="add-voucher-type-btn">
            <Plus size={15} /> Add Type
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : (
        <>
          {/* Bulk Actions */}
          {selected.size > 0 && (
            <div className="flex items-center gap-3 mb-3 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5" data-testid="bulk-action-bar">
              <span className="text-sm text-red-700 font-medium">{selected.size} selected</span>
              <button onClick={handleBulkDelete} disabled={bulkDeleting}
                className="flex items-center gap-1.5 text-xs bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                data-testid="bulk-delete-vtypes-btn">
                <Trash2 size={12} /> {bulkDeleting ? 'Deleting...' : 'Delete Selected'}
              </button>
              <button onClick={() => setSelected(new Set())} className="text-xs text-gray-500 hover:text-gray-700 ml-auto">Clear</button>
            </div>
          )}
          {types.length > 0 && (
            <div className="flex items-center gap-2 mb-3">
              <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                <input type="checkbox" checked={selected.size > 0 && selected.size === types.length} onChange={toggleAll}
                  className="rounded border-gray-300 accent-[#EC1A23]" data-testid="select-all-vtypes" />
                Select All
              </label>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {types.length === 0 ? (
              <div className="col-span-3 text-center py-16 text-gray-400">No voucher types yet</div>
            ) : types.map(type => (
              <div key={type.id} className={`bg-white rounded-2xl overflow-hidden border shadow-sm relative ${selected.has(type.id) ? 'border-red-300 ring-1 ring-red-200' : 'border-gray-100'}`} data-testid="voucher-type-card">
                <div className="absolute top-3 left-3 z-10">
                  <input type="checkbox" checked={selected.has(type.id)} onChange={() => toggleSelect(type.id)}
                    className="rounded border-gray-300 accent-[#EC1A23] w-4 h-4 shadow-sm" data-testid="select-vtype" />
                </div>
              {type.artwork_url ? (
                <img src={`${API_BASE}${type.artwork_url}`} alt={type.name} className="w-full h-32 object-cover" />
              ) : type.image_url ? (
                <img src={type.image_url.startsWith('http') ? type.image_url : `${API_BASE}${type.image_url}`} alt={type.name} className="w-full h-32 object-cover" />
              ) : (
                <div className="h-2" style={{ background: 'linear-gradient(90deg, #561D1A, #EC1A23, #D4AF37)' }} />
              )}
              <div className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium text-gray-800">{type.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${type.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                    {type.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {(() => {
                  const cat = VOUCHER_CATEGORIES.find(c => c.value === (type.voucher_category || 'gift'));
                  const Icon = cat?.icon || Gift;
                  return (
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: `${cat?.color || '#D4AF37'}18`, color: cat?.color || '#D4AF37' }}>
                        <Icon size={10} /> {cat?.label || 'Gift'}
                      </span>
                      {type.voucher_category === 'cashback' && type.cashback_percent && (
                        <span className="text-[10px] text-gray-400">{type.cashback_percent}% back</span>
                      )}
                      {type.voucher_category === 'milestone' && type.milestone_threshold && (
                        <span className="text-[10px] text-gray-400">at {formatAmount(type.milestone_threshold, activeCurrency)}</span>
                      )}
                      {type.voucher_category === 'event' && type.campaign_end && (
                        <span className="text-[10px] text-gray-400">ends {formatShortDate(type.campaign_end)}</span>
                      )}
                    </div>
                  );
                })()}
                <p className="text-xs text-gray-400 mb-3 line-clamp-2">{type.description}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {type.allowed_amounts?.map(amt => (
                    <span key={amt} className="text-xs bg-[#561D1A]/5 text-[#561D1A] px-2 py-0.5 rounded-full">
                      {formatAmount(amt, activeCurrency)}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <p className="text-xs text-gray-400">{type.store_ids?.length || 0} store(s)</p>
                  {type.artwork_url && (
                    <span className="text-xs bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-0.5 rounded-full flex items-center gap-1">
                      <ImageIcon size={10} /> Artwork
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => openEdit(type)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#561D1A] px-3 py-1.5 rounded-lg border border-gray-200 hover:border-[#561D1A]/30" data-testid="edit-voucher-type-btn">
                    <Edit2 size={12} /> Edit
                  </button>
                  <button onClick={() => openCreateVouchers(type)} className="flex items-center gap-1 text-xs text-[#EC1A23] hover:text-white hover:bg-[#EC1A23] px-3 py-1.5 rounded-lg border border-[#EC1A23]/30 transition-colors" data-testid="generate-vouchers-btn">
                    <Ticket size={12} /> Generate
                  </button>
                  <button onClick={async () => {
                    const newVal = !(type.show_on_shop !== false);
                    await api.put(`/api/voucher/admin/voucher-types/${type.id}`, { show_on_shop: newVal });
                    toast.success(newVal ? 'Visible on shop' : 'Hidden from shop');
                    load();
                  }}
                    className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border transition-colors ${type.show_on_shop !== false ? 'text-emerald-600 border-emerald-200 bg-emerald-50 hover:bg-emerald-100' : 'text-gray-400 border-gray-200 hover:bg-gray-50'}`}
                    data-testid="toggle-shop-visibility-btn">
                    {type.show_on_shop !== false ? <><Eye size={12} /> Visible</> : <><EyeOff size={12} /> Hidden</>}
                  </button>
                  <button onClick={() => handleDelete(type)} disabled={deleting === type.id}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-50 text-red-500 border-red-200 bg-red-50 hover:bg-red-100"
                    data-testid="delete-voucher-type-btn"
                    title="Delete permanently">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {modalOpen && (
        <Modal title={editType ? 'Edit Voucher Type' : 'Create Voucher Type'} onClose={() => setModalOpen(false)}>
          <form onSubmit={handleSave} className="space-y-4">
            {/* Category Picker */}
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">Voucher Category *</label>
              <div className="grid grid-cols-2 gap-2">
                {VOUCHER_CATEGORIES.map(cat => {
                  const Icon = cat.icon;
                  const selected = form.voucher_category === cat.value;
                  return (
                    <button key={cat.value} type="button" onClick={() => setForm(f => ({ ...f, voucher_category: cat.value }))}
                      className={`flex items-center gap-2 p-2.5 rounded-xl border-2 text-left transition-all text-xs ${selected ? 'border-current shadow-sm' : 'border-gray-100 hover:border-gray-200'}`}
                      style={selected ? { borderColor: cat.color, background: `${cat.color}08`, color: cat.color } : {}}
                      data-testid={`cat-${cat.value}`}>
                      <Icon size={14} />
                      <div>
                        <div className="font-medium" style={selected ? { color: cat.color } : { color: '#333' }}>{cat.label}</div>
                        <div className="text-[10px] text-gray-400">{cat.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Name *</label>
              <input required value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                placeholder="e.g. Universal Gift Voucher"
                className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="vtype-name-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                rows={2} className="w-full border-2 border-gray-200 focus:border-[#561D1A] p-2 text-sm outline-none rounded-xl bg-transparent resize-none" />
            </div>

            {/* Amounts - show for all purchasable categories */}
            {form.voucher_category !== 'cashback' && form.voucher_category !== 'milestone' && (
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Allowed Amounts (comma-separated)</label>
                <input value={form.allowed_amounts} onChange={e => setForm(f => ({...f, allowed_amounts: e.target.value}))}
                  placeholder="500,1000,2000,5000"
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent font-mono" data-testid="vtype-amounts-input" />
              </div>
            )}

            {/* ── Cashback-specific fields ── */}
            {form.voucher_category === 'cashback' && (
              <div className="bg-emerald-50/50 rounded-xl p-4 space-y-3 border border-emerald-100">
                <h4 className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Cashback Configuration</h4>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Cashback Percentage (%)</label>
                  <input type="number" min="0.1" max="100" step="0.1" value={form.cashback_percent}
                    onChange={e => setForm(f => ({...f, cashback_percent: e.target.value}))}
                    placeholder="e.g. 5"
                    className="w-full border-b-2 border-emerald-200 focus:border-emerald-600 py-2 text-sm outline-none bg-transparent" data-testid="cashback-percent-input" />
                  <p className="text-[10px] text-gray-400 mt-1">Shopper gets this % back as a voucher after purchase</p>
                </div>
              </div>
            )}

            {/* ── Category-restriction fields ── */}
            {form.voucher_category === 'category' && (
              <div className="bg-violet-50/50 rounded-xl p-4 space-y-3 border border-violet-100">
                <h4 className="text-xs font-semibold text-violet-700 uppercase tracking-wider">Category Restrictions</h4>
                <p className="text-[10px] text-gray-400">Only redeemable at stores with these categories</p>
                <div className="flex flex-wrap gap-2">
                  {STORE_CATEGORIES.map(cat => (
                    <button key={cat} type="button" onClick={() => toggleStoreCategory(cat)}
                      className={`text-xs px-3 py-1.5 rounded-full border-2 transition-all ${form.allowed_store_categories.includes(cat) ? 'bg-violet-600 border-violet-600 text-white' : 'border-gray-200 text-gray-600 hover:border-violet-400'}`}
                      data-testid={`store-cat-${cat}`}>
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Milestone-specific fields ── */}
            {form.voucher_category === 'milestone' && (
              <div className="bg-orange-50/50 rounded-xl p-4 space-y-3 border border-orange-100">
                <h4 className="text-xs font-semibold text-orange-700 uppercase tracking-wider">Milestone Configuration</h4>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Spend Threshold ({symbol})</label>
                  <input type="number" min="100" step="100" value={form.milestone_threshold}
                    onChange={e => setForm(f => ({...f, milestone_threshold: e.target.value}))}
                    placeholder="e.g. 20000"
                    className="w-full border-b-2 border-orange-200 focus:border-orange-600 py-2 text-sm outline-none bg-transparent" data-testid="milestone-threshold-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Reward Amount ({symbol})</label>
                  <input type="number" min="50" step="50" value={form.milestone_reward_amount}
                    onChange={e => setForm(f => ({...f, milestone_reward_amount: e.target.value}))}
                    placeholder="e.g. 1000"
                    className="w-full border-b-2 border-orange-200 focus:border-orange-600 py-2 text-sm outline-none bg-transparent" data-testid="milestone-reward-input" />
                  <p className="text-[10px] text-gray-400 mt-1">Voucher value auto-issued when threshold is hit</p>
                </div>
              </div>
            )}

            {/* ── Event/Campaign fields ── */}
            {form.voucher_category === 'event' && (
              <div className="bg-sky-50/50 rounded-xl p-4 space-y-3 border border-sky-100">
                <h4 className="text-xs font-semibold text-sky-700 uppercase tracking-wider">Campaign Period</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Start</label>
                    <input type="datetime-local" value={form.campaign_start}
                      onChange={e => setForm(f => ({...f, campaign_start: e.target.value}))}
                      className="w-full border-b-2 border-sky-200 focus:border-sky-600 py-2 text-xs outline-none bg-transparent" data-testid="campaign-start-input" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">End</label>
                    <input type="datetime-local" value={form.campaign_end}
                      onChange={e => setForm(f => ({...f, campaign_end: e.target.value}))}
                      className="w-full border-b-2 border-sky-200 focus:border-sky-600 py-2 text-xs outline-none bg-transparent" data-testid="campaign-end-input" />
                  </div>
                </div>
              </div>
            )}

            {/* ── Tenant-sponsored fields ── */}
            {form.voucher_category === 'tenant_sponsored' && stores.length > 0 && (
              <div className="bg-pink-50/50 rounded-xl p-4 space-y-3 border border-pink-100">
                <h4 className="text-xs font-semibold text-pink-700 uppercase tracking-wider">Sponsoring Tenant</h4>
                <select value={form.sponsor_store_id} onChange={e => setForm(f => ({...f, sponsor_store_id: e.target.value}))}
                  className="w-full border-b-2 border-pink-200 focus:border-pink-600 py-2 text-sm outline-none bg-transparent" data-testid="sponsor-store-select">
                  <option value="">— Select sponsoring store —</option>
                  {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <p className="text-[10px] text-gray-400">This store funds the voucher; LuxeMall handles issuance and redemption</p>
              </div>
            )}

            {/* Display Image Upload */}
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">Display Image (Shop card)</label>
              {form.image_url && (
                <div className="mb-2 relative inline-block">
                  <img src={form.image_url.startsWith('http') ? form.image_url : `${API_BASE}${form.image_url}`} alt="Display" className="h-20 rounded-lg object-cover" />
                  <button type="button" onClick={() => setForm(f => ({...f, image_url: ''}))}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">
                    <X size={10} />
                  </button>
                </div>
              )}
              <input type="file" ref={imageInputRef} accept="image/*" onChange={e => handleUpload(e.target.files[0], 'image')} className="hidden" />
              <button type="button" onClick={() => imageInputRef.current?.click()} disabled={uploadingImage}
                className="flex items-center gap-2 text-xs border border-dashed border-gray-300 text-gray-500 hover:border-[#561D1A] hover:text-[#561D1A] px-3 py-2 rounded-lg transition-colors"
                data-testid="upload-image-btn">
                {uploadingImage ? 'Uploading...' : <><Upload size={12} /> Upload Image</>}
              </button>
            </div>

            {/* Artwork Template Upload */}
            <div className="border-t border-gray-100 pt-4">
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Voucher Artwork Template</label>
              <p className="text-xs text-gray-400 mb-2">Upload the artwork background. Shopper details (name, amount, QR code, expiry) will be overlaid on purchase.</p>
              {form.artwork_url && (
                <div className="mb-2 relative inline-block">
                  <img src={`${API_BASE}${form.artwork_url}`} alt="Artwork" className="h-24 rounded-lg object-cover border border-[#D4AF37]/30" />
                  <button type="button" onClick={() => setForm(f => ({...f, artwork_url: ''}))}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">
                    <X size={10} />
                  </button>
                </div>
              )}
              <input type="file" ref={artworkInputRef} accept="image/*" onChange={e => handleUpload(e.target.files[0], 'artwork')} className="hidden" />
              <button type="button" onClick={() => artworkInputRef.current?.click()} disabled={uploadingArtwork}
                className="flex items-center gap-2 text-xs border border-dashed border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/5 px-3 py-2 rounded-lg transition-colors"
                data-testid="upload-artwork-btn">
                {uploadingArtwork ? 'Uploading...' : <><ImageIcon size={12} /> Upload Artwork Template</>}
              </button>
            </div>

            {stores.length > 0 && (
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">Applicable Stores</label>
                <div className="flex flex-wrap gap-2">
                  {stores.map(s => (
                    <button type="button" key={s.id} onClick={() => toggleStore(s.id)}
                      className={`text-xs px-3 py-1.5 rounded-full border-2 transition-all ${form.store_ids.includes(s.id) ? 'bg-[#561D1A] border-[#561D1A] text-white' : 'border-gray-200 text-gray-600 hover:border-[#561D1A]/40'}`}>
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button type="submit" disabled={saving} className="w-full bg-[#EC1A23] hover:bg-[#C4121A] disabled:opacity-60 text-white py-3 rounded-full text-sm font-medium" data-testid="save-vtype-btn">
              {saving ? 'Saving...' : editType ? 'Update Type' : 'Create Type'}
            </button>
          </form>
        </Modal>
      )}

      {/* Generate Vouchers Modal */}
      {createVoucherModal && (
        <Modal title={`Generate Vouchers — ${createVoucherModal.name}`} onClose={() => setCreateVoucherModal(null)}>
          <form onSubmit={handleCreateVouchers} className="space-y-4">
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Amount ({symbol}) *</label>
              {createVoucherModal.allowed_amounts?.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-1">
                  {createVoucherModal.allowed_amounts.map(amt => (
                    <button type="button" key={amt} onClick={() => setVoucherForm(f => ({...f, amount: String(amt)}))}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                        String(voucherForm.amount) === String(amt)
                          ? 'bg-[#561D1A] text-white'
                          : 'bg-gray-50 text-gray-700 border border-gray-200 hover:border-[#561D1A]/40'
                      }`}
                      data-testid={`amount-${amt}`}
                    >
                      {formatAmount(amt, activeCurrency)}
                    </button>
                  ))}
                </div>
              ) : (
                <input required type="number" value={voucherForm.amount} onChange={e => setVoucherForm(f => ({...f, amount: e.target.value}))}
                  placeholder="e.g. 1000"
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="create-voucher-amount" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Quantity</label>
                <input type="number" min={1} max={100} value={voucherForm.quantity} onChange={e => setVoucherForm(f => ({...f, quantity: e.target.value}))}
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="create-voucher-qty" />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Expiry (days)</label>
                <input type="number" min={1} value={voucherForm.expiry_days} onChange={e => setVoucherForm(f => ({...f, expiry_days: e.target.value}))}
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" />
              </div>
            </div>
            <button type="submit" disabled={creatingVouchers || !voucherForm.amount}
              className="w-full bg-[#EC1A23] hover:bg-[#C4121A] disabled:opacity-60 text-white py-3 rounded-full text-sm font-medium" data-testid="save-create-vouchers-btn">
              {creatingVouchers ? 'Generating...' : `Generate ${voucherForm.quantity} Voucher(s)`}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
